import React, { useState } from "react";
import Select from "react-select";
import { Col, Row, Label, Input, Button } from "reactstrap";
import {
  CreateNewScormLicenseCourse,
  CreateNewScormLicenseLicenses,
} from "../../actions/superUserMutations";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { GetLicenseManagementCustomers } from "../../actions/superUserQueries";
import { FormattedMessage, useIntl } from "react-intl";
import { formatServiceError } from "../../Utilities/serviceErrorHelper";

function LicMgtAddLicenseForm({
  courses,
  customer,
  handleUpdateData,
  setAddLicenseOpen,
}) {
  const intl = useIntl();
  const [addNewCourseIsOpen, setaddNewCourseIsOpen] = useState(false);
  const [newCourse, setNewCourse] = useState({ name: "" });
  const [newLicense, setNewLicense] = useState({ quantity: 1 });

  const licenseCustomers = GetLicenseManagementCustomers();

  const handleAddNewScormLicenseCourse = useMutation({
    mutationFn: CreateNewScormLicenseCourse,
  });

  const handleCreateNewLicenses = useMutation({ mutationFn: CreateNewScormLicenseLicenses });
  const queryClient = useQueryClient();

  const handleAddLicense = () => {
    if (!newLicense.course) {
      toast.warn(intl.formatMessage({ id: "YouMustChooseACourse" }));
      return;
    }

    const quantity = Number(newLicense.quantity);
    if (!Number.isInteger(quantity) || quantity < 1 || quantity > 100) {
      toast.warn(intl.formatMessage({ id: "InvalidNumberOfLicenses" }));
      return;
    }

    handleCreateNewLicenses.mutate(
      {
        courseId: newLicense.course.id,
        customerId: customer.id,
        quantity: quantity,
      },
      {
        onSuccess: (data) => {
          toast.success(intl.formatMessage({ id: "LicenseAdded" }));
          let tempCustomerData = [...licenseCustomers.data];
          let itemIndex = tempCustomerData.findIndex(
            (cust) => cust.customer.id === customer.id
          );
          tempCustomerData[itemIndex].licensesPerCourse =
            data.data.result.licensesPerCourse;
          queryClient.setQueryData(
            ["FetchLicenseManagementCustomers"],
            tempCustomerData
          );
          handleUpdateData();
          setAddLicenseOpen(false);
        },
        onError: (err) => {
          toast.warn(formatServiceError(intl, err?.response?.data));
        },
      }
    );
  };

  const handleAddNewCourse = () => {
    if (newCourse.name.length > 0) {
      handleAddNewScormLicenseCourse.mutate(
        {
          name: newCourse.name,
          identifier: newCourse.identifier,
          additionalInfo: newCourse.additionalInfo,
        },
        {
          onSuccess: (data) => {
            toast.success(intl.formatMessage({ id: "CourseCreated" }));
            setaddNewCourseIsOpen(false);
            queryClient.setQueryData(
              ["FetchLicenseManagementCourses"],
              [...courses.data, data.data.result]
            );
          },
          onError: (err) => {
            toast.warn(formatServiceError(intl, err?.response?.data));
          },
        }
      );
    }
  };

  return (
    <>
      {addNewCourseIsOpen ? (
        <>
          <Row className="mt-3 mb-1">
            <Col>
              <Label className="me-2 mb-0" for="newCourseName">
                <FormattedMessage id="CourseName" />:
              </Label>
              <Input
                name="newCourseName"
                type="text"
                onChange={(data) =>
                  setNewCourse({ ...newCourse, name: data.target.value })
                }
              />
            </Col>
            <Col>
              <Label className="me-2 mb-0" for="newCourseIdentifier">
                <FormattedMessage id="IdentifierForCourse" />:
              </Label>
              <Input
                name="newCourseIdentifier"
                type="text"
                onChange={(data) =>
                  setNewCourse({ ...newCourse, identifier: data.target.value })
                }
              />
            </Col>
            <Col>
              <Label className="me-2 mb-0" for="newCourseAdditionalInfo">
                <FormattedMessage id="AdditionalInfo" />:
              </Label>
              <Input
                name="newCourseAdditionalInfo"
                type="text"
                onChange={(data) =>
                  setNewCourse({
                    ...newCourse,
                    additionalInfo: data.target.value,
                  })
                }
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Button
                color="secondary"
                className="me-2"
                onClick={() => setaddNewCourseIsOpen(false)}
              >
                <FormattedMessage id="Cancel" />
              </Button>
              <Button
                outline
                color="primary"
                onClick={() => handleAddNewCourse()}
              >
                <FormattedMessage id="AddCourse" />
              </Button>
            </Col>
          </Row>
        </>
      ) : (
        <Row className="align-items-end">
          <Col>
            <Label for="selectCourse">
              <FormattedMessage id="ChooseCourse" />:{" "}
            </Label>
            <Select
              name="selectCourse"
              options={courses.data
                .filter(
                  (mod) =>
                    !licenseCustomers.data
                      .find((licCust) => licCust.customer.id === customer.id)
                      .licensesPerCourse.some(
                        (licmod) => licmod.course.id === mod.id
                      )
                )
                .map((option) => ({
                  ...option,
                  label: option.name,
                  value: option,
                  clearableValue: true,
                }))}
              onChange={(data) =>
                setNewLicense({ ...newLicense, course: data.value })
              }
            ></Select>
          </Col>
          <Col>
            <Label for="quantity">
              <FormattedMessage id="NumberOfLicenses" />:{" "}
            </Label>{" "}
            <Input
              style={{ height: "38px" }}
              className="rounded"
              type="number"
              min={1}
              max={100}
              step={1}
              value={newLicense.quantity}
              onChange={(data) =>
                setNewLicense({ ...newLicense, quantity: data.target.value })
              }
            ></Input>
          </Col>
          <Col>
            <Button
              onClick={() => handleAddLicense()}
              className="float-end"
              outline
              color="success"
            >
              <FormattedMessage id="Save" />
            </Button>
          </Col>
        </Row>
      )}
    </>
  );
}

export default LicMgtAddLicenseForm;
